/* eslint-disable @typescript-eslint/naming-convention */
import { API_ENDPOINTS } from '@/utils/constants/end-points';
import { buildQueryParams } from '@/utils/service-utils';

import apiClient from '../api-client';

// MARK: Mock data for development when API is not live
const MOCK_CONDITIONS_DATA = {
  performancePeriodEndDate: '2026-04-30T00:00:00',
  isConditionMet: null,
  totalAchievementFactor: null,
  conditions: [
    {
      id: 58,
      measureName: 'Absolute Total Shareholder Return',
      weight: 35,
      minimum: 2,
      minimumFactor: 0.3,
      threshold: 6,
      thresholdFactor: 1,
      target: 8,
      targetFactor: 2,
      superior: 10,
      superiorFactor: 3,
      achievement: null,
      achievementFactor: null,
    },
    {
      id: 59,
      measureName: 'EBG New Revenue Streams by 2025',
      weight: 65,
      minimum: 1,
      minimumFactor: 0.3,
      threshold: 1.5,
      thresholdFactor: 1,
      target: 2,
      targetFactor: 2,
      superior: 3,
      superiorFactor: 3,
      achievement: null,
      achievementFactor: null,
    },
  ],
};

const MOCK_COMPUTE_RESPONSE = {
  conditions: [
    {
      conditionId: 58,
      achievement: 1.1,
      calculatedFactor: 0,
    },
    {
      conditionId: 59,
      achievement: 5.5,
      calculatedFactor: 3,
    },
  ],
  totalAchievementFactor: 1.95,
};

const MOCK_UPLOAD_RESPONSE = {
  awardNo: 'PBAward002',
  fileName: 'FinalAwardServiceDay.csv',
  globalErrors: [],
  invalidRecords: 1,
  totalRecords: 100,
  validRecords: 99,
  message: 'Service day file processed.',
  success: true,
};

const MOCK_REVIEW_DATA = {
  items: [
    {
      id: 12,
      fullName: 'Venkatesh',
      employeeId: 'EMP001',
      value: 10,
      status: 'Failure',
      errorMessages: ['The award for this user is already lapsed'],
    },
    {
      id: 13,
      fullName: 'John Doe',
      employeeId: 'EMP002',
      value: 15,
      status: 'Success',
      errorMessages: [],
    },
  ],
  totalCount: 2,
  readyCount: 1,
  exceptionCount: 1,
  offset: 0,
  limit: 10,
};

export const fetchFinalAwardList = async (params: {
  offset?: number;
  limit?: number;
  searchField?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  isExport?: boolean;
  StatusId?: number;
}) => {
  const searchParams = buildQueryParams(params);
  try {
    const res = await apiClient.get(
      `${API_ENDPOINTS?.FINALAWARD}?${searchParams.toString()}`
    );
    return res?.data?.data;
  } catch (error) {
    console.warn('API not live, returning empty list fallback', error);
    return { items: [], totalCount: 0 };
  }
};

export const fetchConditionsBasedOnAwardId = async (params: {
  awardId: string;
}) => {
  const { awardId } = params;
  try {
    const res = await apiClient.get(
      `${API_ENDPOINTS?.FINALAWARD}/${awardId}/conditions`
    );
    return res?.data?.data;
  } catch (error) {
    console.warn('API not live, using mock conditions data', error);
    return MOCK_CONDITIONS_DATA;
  }
};

export const computeAchievementFactor = async (params: {
  awardId: string;
  payload: any;
}) => {
  const { awardId, payload } = params;
  try {
    const res = await apiClient.post(
      `${API_ENDPOINTS?.FINALAWARD}/${awardId}/compute`,
      payload
    );
    return res?.data?.data;
  } catch (error) {
    console.warn('API not live, using mock compute response', error);
    // Simulate dynamic response based on payload length if needed,
    // but here we return the sample provided.
    return MOCK_COMPUTE_RESPONSE;
  }
};

export const saveAchievementFactor = async (params: {
  awardId: string;
  payload: any;
}) => {
  const { awardId, payload } = params;
  try {
    const res = await apiClient.post(
      `${API_ENDPOINTS?.FINALAWARD}/${awardId}/proceed`,
      payload
    );
    return res?.data?.data;
  } catch (error) {
    console.warn('API not live, simulating successful save', error);
    return {
      success: true,
      message: 'Achievement factors saved successfully.',
    };
  }
};

export const uploadServiceDayFile = async (awardId: string, file: File) => {
  const formData = new FormData();
  formData.append('File', file);
  try {
    const res = await apiClient.post(
      `${API_ENDPOINTS?.FINALAWARD}/${awardId}/upload-serviceday`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return {
      ...res?.data?.data,
      message: res?.data?.message,
      success: res?.data?.success,
    };
  } catch (error) {
    console.warn('API not live, simulating service day upload response', error);
    return MOCK_UPLOAD_RESPONSE;
  }
};

export const uploadAdjustmentFile = async (awardId: string, file: File) => {
  const formData = new FormData();
  formData.append('File', file);
  try {
    const res = await apiClient.post(
      `${API_ENDPOINTS?.FINALAWARD}/${awardId}/upload-adjustment`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return {
      ...res?.data?.data,
      message: res?.data?.message,
      success: res?.data?.success,
    };
  } catch (error) {
    console.warn('API not live, simulating adjustment upload response', error);
    return MOCK_UPLOAD_RESPONSE;
  }
};

export const fetchAdjustmentReviewData = async (
  awardId: string,
  type: string,
  params: any
) => {
  const searchParams = buildQueryParams(params);
  try {
    const res = await apiClient.get(
      `${API_ENDPOINTS?.FINALAWARD}/${awardId}/review/${type}?${searchParams.toString()}`
    );
    return res?.data?.data;
  } catch (error) {
    console.warn('API not live, using mock review data', error);
    return MOCK_REVIEW_DATA;
  }
};
export const discardFinalAwardFile = async (
  awardId: string,
  type: 'serviceday' | 'adjustment'
) => {
  const res = await apiClient.delete(
    `${API_ENDPOINTS?.FINALAWARD}/${awardId}/discard/${type}`
  );
  return res?.data;
};
export const fetchAdjustmentSummary = async (awardId: string) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS?.FINALAWARD}/${awardId}/adjustment-summary`
  );
  return res?.data?.data;
};

export const downloadFinalAwardGeneratedFile = async (awardId: string) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS?.FINALAWARD}/${awardId}/preview-file`,
    { responseType: 'blob' }
  );
  const url = globalThis.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `Final Award List - ${awardId}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};

export const downloadAdjustmentFileAsBlob = async (
  awardId: string,
  type: 'serviceday' | 'adjustment',
  fileName: string
) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS?.FINALAWARD}/${awardId}/download/${type}`,
    { responseType: 'blob' }
  );
  const url = globalThis.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};
export const fetchFinalAwardApprovalList = async (params: any) => {
  const mappedParams = {
    Offset: (params.offset || 0) * (params.limit || 10),
    Limit: params.limit || 10,
    SearchField: params.searchField || params.search || '',
    SortBy: params.sortBy || 'awardNo',
    SortDir: params.sortDir || 'asc',
    workflowStatusId: params.workflowStatusId,
    StatusId: params.StatusId,
  };

  const res = await apiClient.get(`${API_ENDPOINTS?.FINALAWARD}/approvallist`, {
    params: mappedParams,
  });
  return res?.data?.data;
};

export const fetchFinalAwardSummary = async (awardNo: string) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS?.FINALAWARD}/${awardNo}/summary`
  );
  return res?.data?.data;
};

export const fetchFinalAwardSummaryForView = async (awardNo: string) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS?.FINALAWARD}/${awardNo}/summary/view`
  );
  return res?.data?.data;
};

/**
 * Final Award Actions (Approve/Reject/Revise)
 */
export const actionFinalAward = async (
  awardNo: string,
  data: { action: string; remarks: string }
) => {
  try {
    const url = `${API_ENDPOINTS.FINALAWARD}/${awardNo}/action`;
    const res = await apiClient.post(url, data);
    return res.data;
  } catch (error) {
    console.error('Failed to perform action on final award:', error);
    throw error;
  }
};

export const sendFinalAwardForApproval = async (
  awardNo: string,
  payload: {
    awardLetterTypeId: number;
    awardLetterId: number;
    coverLetterId: number;
  }
) => {
  const res = await apiClient.post(
    `${API_ENDPOINTS?.FINALAWARD}/${awardNo}/send-for-approval`,
    payload
  );
  return res?.data;
};
